import Auth from '../api/auth';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const BILLING_LIMIT_ERROR = 'billing_limit_reached';

export const parseErrorCode = error => {
  const { status, data } = error?.response || {};

  if (status === 402 && data?.error === BILLING_LIMIT_ERROR) {
    emitter.emit(BUS_EVENTS.BILLING_LIMIT_REACHED, data);
  }

  return Promise.reject(error);
};

// Mensagem que o axios/JS geram sozinhos. Era o que o usuario via no alerta
// quando a API respondia 500: "Request failed with status code 500".
const TECHNICAL_MESSAGE =
  /^(request failed with status code|network error|timeout of |[a-z]*error:)/i;
// Codigo de maquina (billing_limit_reached) e sentinela de excecao
// (ERROR_WITH_MESSAGE, DUPLICATE_CONTACT) nao sao mensagem: quem trata esses
// codigos sao os proprios fluxos, e a tela tem um texto traduzido melhor.
const MACHINE_CODE = /^[a-z0-9]+(_[a-z0-9]+)+$/i;

const isReadableMessage = value =>
  typeof value === 'string' &&
  value.trim() !== '' &&
  !MACHINE_CODE.test(value.trim()) &&
  !TECHNICAL_MESSAGE.test(value.trim());

/**
 * Texto de erro que da para mostrar ao usuario, ou '' quando nao existe nenhum.
 *
 * Cobre as tres formas em que um erro chega na tela: o erro cru do axios, o
 * ExceptionWithMessage (que guarda o texto em `data` e usa `message` como
 * sentinela) e um Error comum. Quando devolve '', quem chamou usa o texto
 * traduzido da propria tela — nunca o detalhe interno da requisicao.
 */
export const parseAPIErrorMessage = error => {
  const { data } = error?.response || {};
  const candidate = [
    data?.message,
    data?.error,
    error?.data,
    error?.message,
  ].find(isReadableMessage);

  return candidate ? candidate.trim() : '';
};

const getCurrentLocale = () => {
  return (
    window.WOOT?.$root?.$i18n?.locale ||
    window.chatwootConfig?.selectedLocale ||
    'pt_BR'
  );
};

export default axios => {
  const { apiHost = '' } = window.chatwootConfig || {};
  const wootApi = axios.create({ baseURL: `${apiHost}/` });
  // Add Auth Headers to requests if logged in
  if (Auth.hasAuthCookie()) {
    const {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    } = Auth.getAuthData();
    Object.assign(wootApi.defaults.headers.common, {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    });
  }
  wootApi.interceptors.request.use(config => {
    const locale = getCurrentLocale();
    const nextConfig = { ...config };

    nextConfig.params = {
      ...(config.params || {}),
      locale: config.params?.locale || locale,
    };

    nextConfig.headers = {
      ...(config.headers || {}),
      'Accept-Language': locale,
    };

    return nextConfig;
  });
  // Response parsing interceptor
  wootApi.interceptors.response.use(
    response => response,
    error => parseErrorCode(error)
  );
  return wootApi;
};
